/**
 * Which "how to switch the camera back on" instructions the blocked-camera
 * card should show, and when a user who keeps hitting that card should be
 * reported as stuck.
 *
 * Kept apart from the card component so both rules can be unit-tested as
 * plain functions, with no Angular and no browser — the same reason
 * `services/scan-error-policy.ts` is its own module.
 */

/** Which camera screen showed the blocked-camera card. Named here rather than
 *  beside the service so this module, which deliberately knows nothing about
 *  Angular, can still type its own callers. */
export enum ScannerName {
  Barcode = 'barcode',
  Label = 'label',
  MealPhoto = 'meal-photo',
  Recipe = 'recipe',
  Equipment = 'equipment',
  WorkoutPlan = 'workout-plan',
}

/** The one set of steps the card shows. Each names a different place the user
 *  has to go: the phone's own app settings on a native build, or — on the web —
 *  whichever settings screen the browser they are actually using keeps its
 *  camera permission on. */
export enum CameraPermissionHelpVariant {
  NativeAndroid = 'native_android',
  NativeIos = 'native_ios',
  WebIosSafari = 'web_ios_safari',
  WebIosOtherBrowser = 'web_ios_other_browser',
  WebMacSafari = 'web_mac_safari',
  WebOther = 'web_other',
}

/**
 * Pick the instructions for this build.
 *
 * `platform` is Capacitor's own answer: 'android' and 'ios' mean we are inside
 * the installed app, 'web' means a browser. The remaining arguments are
 * browser-level checks, because Capacitor answers 'web' for every browser on
 * every device and the web instructions differ by both of these:
 *
 * - `isAppleMobileDevice` — an iPhone or iPad. On iOS every browser app holds
 *   its own camera permission in the phone's Settings app, so the user has to
 *   be sent to the entry for the browser they are actually using.
 * - `isSafariApp` — the browser application is Safari itself, not merely
 *   another browser built on Apple's WebKit engine. `isSafariAppUserAgent` in
 *   `util/safari-user-agent` is what answers it.
 */
export function pickCameraPermissionHelpVariant(
  platform: 'android' | 'ios' | 'web',
  isAppleMobileDevice: boolean,
  isSafariApp: boolean,
): CameraPermissionHelpVariant {
  // if we are inside the installed app, the camera switch lives in the phone's
  // own settings — the browser controls the web arms talk about do not exist here
  if (platform === 'android') {
    return CameraPermissionHelpVariant.NativeAndroid;
  }
  if (platform === 'ios') {
    return CameraPermissionHelpVariant.NativeIos;
  }
  // if this is an iPhone or iPad, the permission lives in the Settings app, under
  // the entry for whichever browser the user opened us in
  if (isAppleMobileDevice) {
    return isSafariApp
      ? CameraPermissionHelpVariant.WebIosSafari
      : CameraPermissionHelpVariant.WebIosOtherBrowser;
  }
  // if this is Safari anywhere else, it is Safari on a Mac — the only other place
  // it runs. Its camera setting is in the Safari menu, not on the address bar.
  if (isSafariApp) {
    return CameraPermissionHelpVariant.WebMacSafari;
  }
  return CameraPermissionHelpVariant.WebOther;
}

/** Blocked attempts in one app session before we treat the user as stuck.
 *  Two is somebody reading the steps and trying once more; the third means they
 *  are going round in circles. (An attempt is not the same as the card being
 *  drawn — `isNewBlockedEpisode` below says which repeats do not count.) */
const BLOCKED_CARD_SHOWS_BEFORE_STUCK = 3;

/**
 * Whether this blocked card is the one that should be reported to Sentry.
 *
 * `showCountThisSession` counts blocked ATTEMPTS since the app started, across
 * every camera screen — the repeats `isNewBlockedEpisode` rejects are not in it.
 * It spans all the scanners because a user who bounces between the barcode and
 * label scanners is just as stuck as one who reopens the same screen. True on
 * exactly one attempt, so a stuck user produces one report rather than a stream
 * of them.
 */
export function shouldReportRepeatedCameraBlock(showCountThisSession: number): boolean {
  return showCountThisSession === BLOCKED_CARD_SHOWS_BEFORE_STUCK;
}

/** How long after a blocked card was counted a repeat of the SAME scanner is
 *  treated as the same episode rather than a new one. */
const SAME_EPISODE_WINDOW_MS = 60_000;

/** The last blocked card that was counted, or undefined when none has been. */
export interface LastCountedBlock {
  scanner: ScannerName;
  atMillis: number;
}

/**
 * Whether this blocked card is a NEW attempt, or the same one being shown
 * again. The card is destroyed and recreated by ordinary UI flips, so counting
 * every creation would report a user as stuck for using a tab; which flips
 * those are, and what this rule costs, is in
 * `design-docs/scanner-shared-components.md`, "Where the camera permission
 * lives".
 */
export function isNewBlockedEpisode(
  lastCounted: LastCountedBlock | undefined,
  scanner: ScannerName,
  nowMillis: number,
): boolean {
  // if nothing has been counted yet, this is certainly new
  if (lastCounted === undefined) {
    return true;
  }
  // if the user moved to a different scanner, that is a fresh attempt
  if (lastCounted.scanner !== scanner) {
    return true;
  }
  return nowMillis - lastCounted.atMillis >= SAME_EPISODE_WINDOW_MS;
}
